/**
 * ResponsiveContainer - Provides consistent responsive layout across all screens
 * 
 * Breakpoints:
 * - Mobile (default): Full width up to 430px
 * - Tablet (md: 768px+): Up to 640px
 * - Large tablet (lg: 1024px+): Up to 768px
 * - Desktop (xl: 1280px+): Up to 896px
 */

export default function ResponsiveContainer({ 
  children, 
  className = '', 
  fullHeight = true,
  centered = true,
  bgColor = 'bg-gray-100',
  contentBg = 'bg-white',
  shadow = true,
  noPadding = false
}) {
  return (
    <div className={`${fullHeight ? 'screen-min-h' : ''} ${bgColor} ${centered ? 'flex justify-center' : ''}`}>
      <div 
        className={`
          w-full 
          max-w-[430px] md:max-w-[640px] lg:max-w-[768px] xl:max-w-[896px] 
          ${fullHeight ? 'screen-min-h' : ''} 
          ${contentBg} 
          ${shadow ? 'shadow-xl' : ''} 
          ${noPadding ? '' : ''} 
          ${className}
        `}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * ResponsiveWrapper - Simple wrapper for auth/splash screens
 * These screens fill the full viewport on all devices (no max-width constraint).
 * v189.2 BT-31: Changed from min-h-screen to h-full to prevent scroll on viewport-fit pages
 * v207.5.0: Hard viewport lock with white background. Auth screens must NEVER show grey
 *   bleed-through at the bottom regardless of viewport size. Internal scroll only kicks in
 *   when content genuinely overflows (small screens / keyboard up); otherwise the white
 *   surface fills the viewport edge-to-edge.
 */
export function ResponsiveWrapper({ children, className = '', safeArea = true, bottomInset = safeArea }) {
  // ★ PROD-1194: a page whose primary button is in a BottomActionBar passes bottomInset={false}:
  //   the bar pays the home-indicator inset itself, and paying it here too would put it twice
  //   under the button (check-viewport-bottom-actions A6 reads this switch).
  // ★★ PROD-1194: NO overflow here. `height: 100%` resolves against PageWrapper's motion.div, which
  //   has only a min-height, so it is `auto`: this div grew to its content and never scrolled, yet
  //   `overflow-y: auto` still made it a scroll container. `position: sticky` sticks to the nearest
  //   scroll container, so a pinned bar inside it stuck to this non-scrolling box and scrolled away
  //   with the page (measured, check-browser-render R27: Sign Up at y 978 of 667). .scroll-wrapper
  //   (App.jsx) is the one scroller, as MobileLayout's v242.91.0 note found for <main>.
  return (
    <div
      className={`w-full bg-white ${className}`}
      style={{
        height: '100%',
        minHeight: '100%',
        ...(safeArea ? { paddingTop: 'env(safe-area-inset-top, 0px)' } : {}),
        ...(bottomInset ? { paddingBottom: 'env(safe-area-inset-bottom, 0px)' } : {}),
      }}
    >
      {children}
    </div>
  )
}
